import React from 'react';

import { Menu } from '@mui/material';
import clsx from 'clsx';
import { AlineTooltip, SelectButton } from 'src/components';
import { OptionsMenuIcon } from 'src/components/icons/OptionsMenuIcon';
import { displayOnHover } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  divider: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  hiddenButton: {
    opacity: 0,
    pointerEvents: 'none',
  },
  option: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    paddingBottom: theme.spacing(0.75),
    paddingTop: theme.spacing(0.75),
  },
  optionText: {
    marginLeft: theme.spacing(1.5),
  },
  optionsMenu: {
    '&:hover': {
      backgroundColor: theme.appColors.selectedBackgroundNav,
    },
    borderRadius: 3,
    height: 32,
    marginLeft: theme.spacing(1),
    minWidth: 'unset',
    width: 32,
  },
  optionsMenuOpen: {
    backgroundColor: theme.appColors.selectedBackgroundNav,
  },
}));

interface ReusableMenuProps {
  anchorEl: HTMLButtonElement | null;
  children: React.ReactNode;
  handleClose: (event: React.MouseEvent) => void;
  handleOpen: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabledTooltipMsg?: string;
}

export function ReusableMenu({
  anchorEl,
  children,
  disabledTooltipMsg = '',
  handleClose,
  handleOpen,
}: ReusableMenuProps): JSX.Element {
  const { classes } = useStyles();

  function stopPropagation(event: React.MouseEvent): void {
    event.stopPropagation();
  }

  function handleClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    stopPropagation(event);
    handleOpen(event);
  }

  return (
    <>
      <AlineTooltip arrow placement="bottom" title={disabledTooltipMsg}>
        <div>
          <SelectButton
            className={clsx(
              classes.optionsMenu,
              Boolean(anchorEl) && classes.optionsMenuOpen,
              !anchorEl && displayOnHover,
              !anchorEl && classes.hiddenButton
            )}
            data-cy="storable-options-button"
            disabled={!!disabledTooltipMsg}
            onClick={handleClick}
          >
            <OptionsMenuIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(anchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        /**
         * Stop propagation of click and double-click so StorableList rows aren't inadvertently selected
         * (e.g., when children menu items are disabled).
         */
        onClick={stopPropagation}
        onClose={handleClose}
        onDoubleClick={stopPropagation}
      >
        {children}
      </Menu>
    </>
  );
}
